import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import {
  ArrowLeft,
  BookOpen,
  FileText,
  Film,
  Images,
  KanbanSquare,
  LayoutDashboard,
  ListVideo,
  MessageSquare,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react'
import { Link } from 'react-router-dom'

import { canvasBoardsListPath } from '@/features/studio/lib/canvas-path'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { PRODUCTION_SHELL_CLASS, PRODUCTION_BORDER_MUTED } from '@/features/studio/lib/production-shell'
import { STUDIO_BASE } from '@/features/studio/lib/studio-paths'
import { ProductionBudgetGate } from '@/features/studio/modals/ProductionBudgetGate'

const navClass = ({ isActive }: { isActive: boolean }) =>
  clsx(
    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition',
    isActive
      ? 'bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/35'
      : 'text-zinc-400 hover:bg-amber-500/5 hover:text-zinc-200',
  )

function NavGroup({ label }: { label: string }) {
  return (
    <p className="mb-1 mt-3 px-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-600 first:mt-0">
      {label}
    </p>
  )
}

export function ProductionLayout() {
  const { productionId = '' } = useParams()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const base = productionBasePath(productionId)

  const path = pathname.split('?')[0] ?? pathname
  const isReviewCenter = /\/reviews$/.test(path)
  const isFullscreenWorkspace =
    /\/script\/\d+\/episodes\//.test(pathname) ||
    /\/shots\/\d+\/episodes\//.test(pathname) ||
    isReviewCenter

  const gate = productionId ? <ProductionBudgetGate productionId={productionId} /> : null

  if (isFullscreenWorkspace) {
    return (
      <div
        className={clsx(
          PRODUCTION_SHELL_CLASS,
          'flex min-h-0 flex-col',
          isReviewCenter ? 'h-full' : 'min-h-[calc(100vh-var(--portal-header-h))]',
        )}
      >
        {gate}
        <Outlet />
      </div>
    )
  }

  return (
    <div className={clsx('flex min-h-[calc(100vh-var(--portal-header-h))] flex-col lg:flex-row', PRODUCTION_SHELL_CLASS)}>
      <aside className={clsx('shrink-0 border-b bg-[#0b0b13]/95 px-4 py-4 lg:w-56 lg:border-b-0 lg:border-r', PRODUCTION_BORDER_MUTED)}>
        <Link
          to={`${STUDIO_BASE}/productions`}
          className="mb-4 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t('production.nav.backToList')}
        </Link>
        <nav className="studio-scrollbar-hidden flex flex-row gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
          <NavLink to={base} end className={navClass}>
            <LayoutDashboard className="h-4 w-4 shrink-0" />
            {t('production.nav.overview')}
          </NavLink>
          <NavLink to={`${base}/team`} className={navClass}>
            <Users className="h-4 w-4 shrink-0" />
            {t('production.nav.team')}
          </NavLink>
          <NavGroup label={t('production.navGroup.pre')} />
          <NavLink to={`${base}/script`} className={navClass}>
            <FileText className="h-4 w-4 shrink-0" />
            {t('production.nav.script')}
          </NavLink>
          <NavLink to={`${base}/bible`} className={navClass}>
            <BookOpen className="h-4 w-4 shrink-0" />
            {t('production.nav.bible')}
          </NavLink>
          <NavGroup label={t('production.navGroup.production')} />
          <NavLink to={`${base}/shots`} className={navClass}>
            <ListVideo className="h-4 w-4 shrink-0" />
            {t('production.nav.shots')}
          </NavLink>
          <NavLink
            to={canvasBoardsListPath(productionId)}
            className={({ isActive }) =>
              navClass({ isActive: isActive || pathname.includes('/canvas-boards') })
            }
          >
            <Sparkles className="h-4 w-4 shrink-0" />
            {t('production.nav.canvas')}
          </NavLink>
          <NavLink to={`${base}/reviews`} className={navClass}>
            <MessageSquare className="h-4 w-4 shrink-0" />
            {t('production.nav.reviews')}
          </NavLink>
          <NavLink to={`${base}/tasks`} className={navClass}>
            <KanbanSquare className="h-4 w-4 shrink-0" />
            {t('production.nav.tasks')}
          </NavLink>
          <NavGroup label={t('production.navGroup.assets')} />
          <NavLink to={`${base}/assets`} className={navClass}>
            <Images className="h-4 w-4 shrink-0" />
            {t('production.nav.assets')}
          </NavLink>
          <NavGroup label={t('production.navGroup.post')} />
          <NavLink to={`${base}/timeline`} className={navClass}>
            <Film className="h-4 w-4 shrink-0" />
            {t('production.nav.timeline')}
          </NavLink>
          <div className="my-2 hidden h-px bg-amber-400/20 lg:block" />
          <NavLink to={`${base}/settings`} className={navClass}>
            <Settings className="h-4 w-4 shrink-0" />
            {t('production.nav.settings')}
          </NavLink>
        </nav>
      </aside>
      <div className="min-w-0 flex-1">
        {gate}
        <Outlet />
      </div>
    </div>
  )
}
